// src/pages/Catalog.tsx
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import ProductCard from '@/components/ui/ProductCard';
import { Product, Category, mapCategoryFromAPI } from '@/lib/types';
import { getProducts } from '@/lib/products';
import { getCategories } from '@/lib/categories';
import { Loader2, Search } from 'lucide-react';

const Catalog = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const categoryId = searchParams.get('category') || undefined;
  const subcategoryId = searchParams.get('subcategory') || undefined;
  const searchQuery = searchParams.get('search') || '';
  const filterParam = searchParams.get('filter') || '';

  const [searchInput, setSearchInput] = useState(searchQuery);
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Fetch Products using the new getProducts function and filters
  const { data: products = [], isLoading } = useQuery<Product[], Error>({
    queryKey: ['products', { categoryId, subcategoryId, search: searchQuery, filter: filterParam }],
    queryFn: async () => {
      const filters = {
        categoryId: categoryId,
        subcategoryId: subcategoryId,
        searchQuery: searchQuery,
        isFeatured: filterParam === 'featured' || undefined,
        isNew: filterParam === 'new' || undefined,
      };
      const fetchedProducts = await getProducts(filters);
      console.log('Produtos carregados no catálogo:', fetchedProducts.length);
      return fetchedProducts;
    },
  });

   // Fetch categories/subcategories *only* if needed for the title
   const { data: categoriesData = [], isLoading: loadingCategories } = useQuery<Category[], Error>({
        queryKey: ["categoriesWithSubcategoriesForTitle"],
        queryFn: getCategories,
        staleTime: 1000 * 60 * 5,
   });

  // Sincronizar searchInput com searchQuery da URL (quando vem do Header)
  useEffect(() => {
    setSearchInput(searchQuery);
  }, [searchQuery]);

  // Debounce para atualizar URL apenas após parar de digitar
  useEffect(() => {
    // Limpar timer anterior
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    // Não fazer nada se o input for igual ao query atual
    if (searchInput === searchQuery) {
      return;
    }

    // Criar novo timer de debounce (800ms)
    debounceTimerRef.current = setTimeout(() => {
      const currentParams = Object.fromEntries(searchParams.entries());
      if (searchInput.trim()) {
        currentParams.search = searchInput.trim();
      } else {
        delete currentParams.search;
      }
      setSearchParams(currentParams, { replace: true });
    }, 800);

    // Cleanup
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [searchInput]);

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Forçar busca imediata ao pressionar Enter
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    const currentParams = Object.fromEntries(searchParams.entries());
    if (searchInput.trim()) {
      currentParams.search = searchInput.trim();
    } else {
      delete currentParams.search;
    }
    setSearchParams(currentParams, { replace: true });
  };


  // Reset all filters
  const handleClearFilters = () => {
    setSearchInput('');
    setSearchParams({}, { replace: true });
  };

  // Get Page Title based on current filters
  const getPageTitle = (): string => {
    if (filterParam === 'featured') return 'Produtos em Destaque';
    if (filterParam === 'new') return 'Novos Produtos';

    if (subcategoryId) {
      const parentCategory = categoriesData.find(cat => cat.id === categoryId);
      const subcat = parentCategory?.subcategories?.find(sub => sub.id === subcategoryId);
      if (subcat) return `${subcat.name}`;
       if (categoryId) {
            const cat = categoriesData.find(c => c.id === categoryId);
            return cat ? `Produtos em ${cat.name}` : 'Catálogo';
       }
    }

    if (categoryId) {
      const cat = categoriesData.find(c => c.id === categoryId);
      return cat ? `Produtos em ${cat.name}` : 'Catálogo';
    }

    if (searchQuery) return `Resultados para "${searchQuery}"`;


    return 'Catálogo de Produtos';
  };

  return (
    <main className="py-8 bg-gray-50">
      <div className="container mx-auto px-4">
        {/* Title */}
        <h1 className="text-3xl font-bold text-gray-900 mb-8">{getPageTitle()}</h1>

        {/* Filters Section */} 
        <form onSubmit={handleSearchSubmit} className="flex flex-col md:flex-row gap-4 mb-8">
          <div className="flex-1 relative">
            <Input
              type="search"
              placeholder="Filtrar produtos nesta página..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              className="w-full pr-10"
              autoComplete="off"
            />
            <button 
              type="submit" 
              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-navy-600 transition-colors"
              aria-label="Buscar"
            >
              <Search size={18} />
            </button>
          </div>
          <div className="flex-none">
            <Button type="button" onClick={handleClearFilters} className="w-full md:w-auto" variant="outline">
              Limpar Filtros
            </Button>
          </div>
        </form>
        {/* --- End Filters Section --- */}

        {/* --- Product List Section --- */} 
        {isLoading ? (
          <div className="text-center py-10">
             <Loader2 className="mx-auto h-12 w-12 animate-spin text-navy-600" />
            <p className="mt-4 text-gray-500">Carregando produtos...</p>
          </div>
        ) : products.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4 md:gap-6">
            {products.map((product) => (
              <ProductCard key={product.id} product={product} />
            ))}
          </div>
        ) : (
          <div className="text-center py-10 bg-white rounded-lg shadow p-6">
            <p className="text-gray-600 font-medium text-lg mb-2">Nenhum produto encontrado.</p>
            {subcategoryId ? (
              <>
                <p className="text-sm text-gray-500 mb-2">Esta subcategoria ainda não possui produtos cadastrados.</p>
                <p className="text-sm text-gray-500 mb-4">Tente ver todos os produtos da categoria ou limpar os filtros.</p>
              </>
            ) : (
              <p className="text-sm text-gray-500 mb-4">Tente ajustar seus filtros ou limpar a busca.</p>
            )}
            <div className="flex flex-col sm:flex-row gap-3 justify-center mt-4">
              {categoryId && subcategoryId && (
                <Button
                  variant="default"
                  onClick={() => {
                    const params = new URLSearchParams(searchParams);
                    params.delete('subcategory');
                    setSearchParams(params, { replace: true });
                  }}
                  className="sm:w-auto"
                >
                  Ver toda a categoria
                </Button>
              )}
              <Button
                variant="secondary"
                onClick={handleClearFilters}
                className="sm:w-auto"
              >
                Ver todos os produtos
              </Button>
            </div>
          </div>
        )}
        {/* --- End Product List Section --- */} 
      </div>
    </main>
  );
};

export default Catalog;
